import { api } from "../api/Middleware";
import { TIME_RANGE_MESSAGE_CONFIG_URL } from "../api/Urls";

/**
 * Service para configuração de Mensagens Automáticas por Range de Horário.
 *
 * Permite configurar uma janela de horário em que o agente envia uma
 * mensagem customizada de forma determinística (sem depender da LLM).
 *
 * Backend endpoint: /api/ai/time-range-message/config/<agent_id>/
 * - GET (auto-cria com defaults se não existir)
 * - PUT (atualiza)
 */
const TimeRangeMessageService = {
  /**
   * @param {number|string} agentId
   * @returns {Promise<Object>} { id, agent, agent_name, enabled, start_time, end_time,
   *                              active_weekdays, message, mode, created_at, updated_at }
   */
  getConfig: async (agentId) => {
    try {
      const response = await api.get(TIME_RANGE_MESSAGE_CONFIG_URL(agentId));
      return response.data;
    } catch (error) {
      console.error("Error fetching time-range message config:", error);
      throw error;
    }
  },

  /**
   * @param {number|string} agentId
   * @param {Object} data
   * @param {boolean} data.enabled
   * @param {string} data.start_time            "HH:MM" or "HH:MM:SS"
   * @param {string} data.end_time              "HH:MM" or "HH:MM:SS"
   * @param {number[]} data.active_weekdays     ints 0-6 (0=Seg ... 6=Dom)
   * @param {string} data.message               aceita [[break]]
   * @param {'once_per_entry'|'every_interaction'|'send_and_deactivate'} data.mode
   */
  updateConfig: async (agentId, data) => {
    try {
      const response = await api.put(TIME_RANGE_MESSAGE_CONFIG_URL(agentId), data);
      return response.data;
    } catch (error) {
      console.error("Error updating time-range message config:", error);
      throw error;
    }
  },
};

export default TimeRangeMessageService;

// =============================================== //
// HELPERS                                         //
// =============================================== //

export const WEEKDAY_OPTIONS = [
  { value: 0, label: "Seg", fullLabel: "Segunda-feira" },
  { value: 1, label: "Ter", fullLabel: "Terça-feira" },
  { value: 2, label: "Qua", fullLabel: "Quarta-feira" },
  { value: 3, label: "Qui", fullLabel: "Quinta-feira" },
  { value: 4, label: "Sex", fullLabel: "Sexta-feira" },
  { value: 5, label: "Sáb", fullLabel: "Sábado" },
  { value: 6, label: "Dom", fullLabel: "Domingo" },
];

export const MODE_OPTIONS = [
  {
    value: "once_per_entry",
    title: "Apenas 1x por entrada na janela",
    description: "Envia a mensagem uma única vez por nova entrada do cliente na janela. A IA continua respondendo normalmente.",
  },
  {
    value: "every_interaction",
    title: "Em toda interação",
    description: "Envia a mensagem em cada batch de interação dentro da janela. A IA continua respondendo normalmente.",
  },
  {
    value: "send_and_deactivate",
    title: "Enviar e desativar bot",
    description: "Envia a mensagem e desativa o bot até o fim da janela. A IA não processa mensagens neste período.",
  },
];

export const DEFAULT_CONFIG = {
  enabled: false,
  start_time: "22:00",
  end_time: "08:00",
  active_weekdays: [0, 1, 2, 3, 4, 5, 6],
  message: "",
  mode: "once_per_entry",
};

/**
 * Normaliza payload do servidor: trunca "HH:MM:SS" -> "HH:MM",
 * aplica defaults se backend retornar null/empty.
 */
export const normalizeServerConfig = (data) => ({
  enabled: !!data.enabled,
  start_time: (data.start_time || DEFAULT_CONFIG.start_time).slice(0, 5),
  end_time: (data.end_time || DEFAULT_CONFIG.end_time).slice(0, 5),
  active_weekdays: Array.isArray(data.active_weekdays) && data.active_weekdays.length
    ? data.active_weekdays
    : DEFAULT_CONFIG.active_weekdays,
  message: data.message || "",
  mode: data.mode || DEFAULT_CONFIG.mode,
});

/**
 * "HH:MM" -> "HH:MM:00" (Django TimeField aceita ambos, mas DRF prefere com seconds).
 */
export const ensureSeconds = (hhmm) => {
  if (!hhmm) return null;
  return hhmm.length === 5 ? `${hhmm}:00` : hhmm;
};

/**
 * Indica se a janela atravessa a meia-noite (start > end).
 */
export const isCrossMidnight = (start, end) => {
  if (!start || !end) return false;
  return start > end;
};
